import React from 'react';
import { useTranslation } from 'react-i18next';
import { getManilaNow, getManilaTodayStr, getManilaMonthStartStr } from '../../lib/dateUtils';

// Format from MANILA calendar components (via getManilaNow(), NOT toISOString/UTC) so presets
// like "This month" / "This year" land on the Asia/Manila day boundary regardless of the
// staffer's device timezone — toISOString would shift a Manila day back/forward and pull the
// wrong day/month/year into DOLE reports.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Preset -> {start,end} anchored on Asia/Manila "today". All-time clears both (backend treats blank as no filter).
function presetRange(key) {
  const now = getManilaNow();
  const end = getManilaTodayStr();
  if (key === 'today') return { start: end, end };
  if (key === 'last7') { const s = new Date(now); s.setDate(s.getDate() - 6); return { start: iso(s), end }; }
  if (key === 'last30') { const s = new Date(now); s.setDate(s.getDate() - 29); return { start: iso(s), end }; }
  if (key === 'month') return { start: getManilaMonthStartStr(), end };
  if (key === 'year') { const s = new Date(now.getFullYear(), 0, 1); return { start: iso(s), end }; }
  if (key === 'all') return { start: '', end: '' };
  return null; // custom
}

const PRESETS = [
  ['today', 'Today'], ['last7', 'Last 7 days'], ['last30', 'Last 30 days'],
  ['month', 'This month'], ['year', 'This year'], ['all', 'All time'], ['custom', 'Custom'],
];

export default function PesoFilterBar({ range, onRangeChange, activePreset, onPresetChange, events = [], selectedEvent = '', onEventChange, showEvents = false }) {
  const { t } = useTranslation();
  // `activePreset` is owned by the parent (each tab keeps its own), so the highlight can never
  // disagree with the shared range across tabs.
  const pick = (key) => {
    onPresetChange(key);
    const r = presetRange(key);
    if (r) onRangeChange(r);
  };
  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => pick(key)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium border transition ${
              activePreset === key ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
            }`}
          >
            {t(`peso.preset_${key}`, label)}
          </button>
        ))}
      </div>
      {activePreset === 'custom' && (
        <div className="flex flex-wrap items-end gap-3 pt-1">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('peso.fromDate', 'From')}</label>
            <input type="date" value={range.start} max={range.end || undefined}
              onChange={(e) => onRangeChange({ ...range, start: e.target.value })}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">{t('peso.toDate', 'To')}</label>
            <input type="date" value={range.end} min={range.start || undefined}
              onChange={(e) => onRangeChange({ ...range, end: e.target.value })}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
        </div>
      )}
      {showEvents && (
        <div className="flex items-center gap-2 pt-1">
          <label className="text-xs text-muted-foreground shrink-0">{t('peso.filterEvent', 'Event')}</label>
          <select
            value={selectedEvent}
            onChange={(e) => onEventChange(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm w-full sm:w-auto sm:max-w-[16rem]"
          >
            <option value="">{t('peso.allEvents', 'All events')}</option>
            {events.map((ev) => (
              <option key={ev.code} value={ev.code}>{ev.name}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
